import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../components/layout';
import ResourceRelationships from '../../components/resources/ResourceRelationships';
import type {
    Application
} from '../../types/knowledge-base';
import { API_ENDPOINTS } from '../../config/api.config';
import PageTransition from '../../components/layout/PageTransition';
import { apiService } from '../../services/apiService';
import type { DataSourceItem, ResourceItem } from '../../components/resources/ResourceRelationships';
import { authService } from "../../services/authService";
import { Alert } from 'react-bootstrap';
import ContentSkeleton from "@/components/loading/ContentSkeleton.tsx";

interface WebSource {
    id: string;
    name: string;
    description?: string;
    configuration: {
        url: string;
        crawl_frequency?: string;
        sitemap_url?: string;
    };
}

interface ApiSource {
    id: string;
    name: string;
    description?: string;
    configuration: {
        endpoint: string;
        method?: string;
    };
}

interface DriveSource {
    id: string;
    name: string;
    description?: string;
    path?: string;
}

interface APIResponse<T> {
    dataSources?: T[];
    sources?: T[];
    webSources?: T[];
    apiSources?: T[];
}

const DashboardPage: React.FC = () => {
    useNavigate();
    const { user: authUser } = useAuth();
    const modalState = useModal();
    const [applications, setApplications] = useState<Application[]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isContentLoading, setIsContentLoading] = useState(false);
    const [resourceData, setResourceData] = useState<{
        dataSources: DataSourceItem[];
        indexes: ResourceItem[];
    }>({
        dataSources: [],
        indexes: []
    });

    const getAllFilesInFolder = async (folderId: string): Promise<DataSourceItem[]> => {
        try {
            const data = await apiService.get<APIResponse<DataSourceItem>>(
                `${API_ENDPOINTS.DATA_SOURCE.LIST}?folder_id=${folderId}`
            );

            let allFiles: DataSourceItem[] = [];

            for (const item of data.dataSources || []) {
                if (item.type === 'folder' || item.item_type === 'folder') {
                    const subFiles = await getAllFilesInFolder(item.id);
                    allFiles = [...allFiles, ...subFiles];
                } else if (item.item_type === 'file' || item.source_type === 'file_upload') {
                    allFiles.push(item);
                }
            }

            return allFiles;
        } catch (error) {
            if (error instanceof Error) {
                console.error('Error fetching folder contents:', error.message);
            }
            return [];
        }
    };

    const fetchData = async () => {
        if (!authService.isAuthenticated()) {
            console.log('Not authenticated, returning');
            return;
        }

        try {
            if (isInitialLoading) {
                setIsContentLoading(false);
            } else {
                setIsContentLoading(true);
            }

            console.log('Starting API calls...');

            const [webData, apiData, driveData, fileData] = await Promise.all([
                apiService.get<APIResponse<WebSource>>(API_ENDPOINTS.DATA_SOURCE.WEB_SOURCES.LIST),
                apiService.get<APIResponse<ApiSource>>(API_ENDPOINTS.DATA_SOURCE.API_SOURCES.LIST),
                apiService.get<APIResponse<DriveSource>>(API_ENDPOINTS.DRIVE.SYNCED),
                apiService.get<APIResponse<DataSourceItem>>(API_ENDPOINTS.DATA_SOURCE.LIST)
            ]);

            console.log('API Responses:', {
                webData,
                apiData,
                driveData,
                fileData
            });

            const homeFolder = fileData.dataSources?.find(
                (item) => item.name === 'Home' &&
                    (item.type === 'folder' || item.item_type === 'folder')
            );

            const fileUploads = homeFolder
                ? await getAllFilesInFolder(homeFolder.id)
                : [];

            const webSources = (webData?.webSources || webData?.dataSources || webData?.sources || [])
                .map((source) => ({
                    ...source,
                    source_type: 'web_source',
                    item_type: 'web_source'
                })) as DataSourceItem[];

            const apiSources = (apiData?.apiSources || apiData?.dataSources || apiData?.sources || [])
                .map((source) => ({
                    ...source,
                    source_type: 'api_source',
                    item_type: 'api_source'
                })) as DataSourceItem[];

            const driveSources = (driveData?.dataSources || driveData?.sources || [])
                .map((source) => ({
                    ...source,
                    source_type: 'drive_sync',
                    item_type: 'drive_sync'
                })) as DataSourceItem[];

            const processedFileUploads = fileUploads.map(file => ({
                ...file,
                source_type: 'file_upload',
                item_type: 'file'
            }));

            const allDataSources = [
                ...processedFileUploads,
                ...webSources,
                ...apiSources,
                ...driveSources
            ];

            const [apps, indexes] = await Promise.all([
                apiService.get<Application[]>(`${API_ENDPOINTS.RAG.LIST}?all=true`),
                apiService.get<ResourceItem[]>(`${API_ENDPOINTS.INDEXES.LIST}?all=true`)
            ]);

            setApplications(apps);
            setResourceData({
                dataSources: allDataSources,
                indexes: indexes || []
            });
        } catch (error) {
            if (error instanceof Error) {
                modalState.setError('Failed to load data: ' + error.message);
            } else {
                modalState.setError('Failed to load data');
            }
        } finally {
            setIsInitialLoading(false);
            setIsContentLoading(false);
        }
    };

    useEffect(() => {
        void fetchData();
    }, []);

    if (!authUser) {
        return (
            <PageTransition>
                <Alert variant="warning">Please log in to view the dashboard.</Alert>
            </PageTransition>
        );
    }

    return (
        <div className="page-container">
            {modalState.error && (
                <Alert variant="danger" onClose={() => modalState.setError(null)} dismissible>
                    {modalState.error}
                </Alert>
            )}
            {modalState.success && (
                <Alert variant="success" onClose={() => modalState.setSuccess(null)} dismissible>
                    {modalState.success}
                </Alert>
            )}

            <div className="mb-4">
                <h5 className="text-lg font-semibold mb-3">Resource Relationships</h5>
                {(isInitialLoading || modalState.isLoading) ? (
                    <ContentSkeleton />
                ) : (
                    <div className="dashboard-card">
                        <ResourceRelationships
                            data={{
                                dataSources: resourceData.dataSources,
                                indexes: resourceData.indexes,
                                ragApplications: applications
                            }}
                            isLoading={isContentLoading}
                            onRefresh={fetchData}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DashboardPage;